import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Button, Badge, Icon } from '../../components/ui';

export default function AccountingEntries() {
  const [mode, setMode] = useState('bank');
  // 'bank' | 'cash' | 'sales' | 'purchases' | 'ar' | 'ap'

  const [accounts, setAccounts] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [loadingLeft, setLoadingLeft] = useState(true);
  const [loadingTx, setLoadingTx] = useState(false);
  const [postingId, setPostingId] = useState(null);
  const [error, setError] = useState(null);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(amount || 0);

  const isTxPosted = (tx) => {
    return tx?.ledgerStatus === 'POSTED' || !!tx?.ledgerEntryId;
  };

  const loadBankAccounts = async () => {
    const res = await api.get('/treasury/bank');
    return Array.isArray(res.data?.accounts) ? res.data.accounts : [];
  };

  const loadCashRegisters = async () => {
    const res = await api.get('/treasury/cash');
    return Array.isArray(res.data?.registers) ? res.data.registers : [];
  };

  const loadClientsFn = async () => {
    try {
      const res = await api.get('/clients');
      return Array.isArray(res.data?.clients) ? res.data.clients :
             Array.isArray(res.data) ? res.data : [];
    } catch {
      return [];
    }
  };

  const loadSuppliersFn = async () => {
    try {
      const res = await api.get('/suppliers');
      return Array.isArray(res.data?.suppliers) ? res.data.suppliers :
             Array.isArray(res.data) ? res.data : [];
    } catch {
      return [];
    }
  };

  const loadTransactions = async ({ type, id }) => {
    if (!id) return;
    try {
      setError(null);
      setLoadingTx(true);

      let endpoint;
      if (type === 'bank') endpoint = `/treasury/bank/${id}/transactions`;
      else if (type === 'cash') endpoint = `/treasury/cash/${id}/transactions`;
      else if (type === 'sales') endpoint = `/invoices?clientId=${id}&limit=100`;
      else if (type === 'purchases') endpoint = `/purchase-invoices?supplierId=${id}&limit=100`;
      else if (type === 'ar') endpoint = `/payments?clientId=${id}&limit=100`;
      else if (type === 'ap') endpoint = `/purchase-payments?supplierId=${id}&limit=100`;

      const res = await api.get(endpoint);

      let txList = [];
      if (res.data) {
        txList = Array.isArray(res.data) ? res.data :
                 Array.isArray(res.data.transactions) ? res.data.transactions :
                 Array.isArray(res.data.invoices) ? res.data.invoices :
                 Array.isArray(res.data.payments) ? res.data.payments :
                 Array.isArray(res.data.items) ? res.data.items : [];
      }

      setTransactions(txList);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setTransactions([]);
    } finally {
      setLoadingTx(false);
    }
  };

  const handlePost = async (tx) => {
    const txId = tx?.id || tx?._id;
    if (!txId) return;

    if (isTxPosted(tx)) {
      toast.info('Déjà postée.');
      return;
    }

    if (postingId === txId) return;

    try {
      setPostingId(txId);
      setError(null);

      let endpoint;
      if (mode === 'bank' || mode === 'cash') {
        endpoint = `/accounting/post/treasury-transaction/${txId}`;
      } else if (mode === 'sales') {
        endpoint = `/accounting/post/sales-invoice/${txId}`;
      } else if (mode === 'purchases') {
        endpoint = `/accounting/post/purchase-invoice/${txId}`;
      } else if (mode === 'ar') {
        endpoint = `/accounting/post/customer-payment/${txId}`;
      } else if (mode === 'ap') {
        endpoint = `/accounting/post/supplier-payment/${txId}`;
      }

      const res = await api.post(endpoint);
      toast.success(`Posté (écriture #${res.data?.entry?.entryNumber || 'OK'}).`);

      setTransactions((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((t) => {
          const id = t?.id || t?._id;
          if (id !== txId) return t;
          return {
            ...t,
            ledgerStatus: 'POSTED',
            ledgerEntryId: res.data?.entry?._id || res.data?.entry?.id,
          };
        });
      });
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info('Déjà postée.');
        setTransactions((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((t) => {
            const id = t?.id || t?._id;
            if (id !== txId) return t;
            return {
              ...t,
              ledgerStatus: 'POSTED',
              ledgerEntryId: e?.response?.data?.ledgerEntryId || t.ledgerEntryId,
            };
          });
        });
        return;
      }
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setPostingId(null);
    }
  };

  const refreshLeft = async () => {
    try {
      setError(null);
      setLoadingLeft(true);

      if (mode === 'bank') {
        const list = await loadBankAccounts();
        setAccounts(list);
        if (!selectedAccount && list.length) setSelectedAccount(list[0]);
      } else if (mode === 'cash') {
        const list = await loadCashRegisters();
        setRegisters(list);
        if (!selectedRegister && list.length) setSelectedRegister(list[0]);
      } else if (mode === 'sales') {
        const list = await loadClientsFn();
        setClients(list);
        if (!selectedClient && list.length) setSelectedClient(list[0]);
      } else if (mode === 'purchases') {
        const list = await loadSuppliersFn();
        setSuppliers(list);
        if (!selectedSupplier && list.length) setSelectedSupplier(list[0]);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoadingLeft(false);
    }
  };

  useEffect(() => {
    setTransactions([]);
    setSelectedAccount(null);
    setSelectedRegister(null);
    setSelectedClient(null);
    setSelectedSupplier(null);
    refreshLeft();
  }, [mode]);

  useEffect(() => {
    if ((mode === 'bank' && selectedAccount) ||
        (mode === 'cash' && selectedRegister) ||
        (mode === 'sales' && selectedClient) ||
        (mode === 'purchases' && selectedSupplier) ||
        (mode === 'ar' && selectedClient) ||
        (mode === 'ap' && selectedSupplier)) {
      const id = mode === 'bank' ? selectedAccount?._id :
                 mode === 'cash' ? selectedRegister?._id :
                 (mode === 'sales' || mode === 'ar') ? selectedClient?._id :
                 selectedSupplier?._id;
      if (id) loadTransactions({ type: mode, id });
    }
  }, [mode, selectedAccount, selectedRegister, selectedClient, selectedSupplier]);

  const selectedLabel = useMemo(() => {
    if (mode === 'bank' && selectedAccount) return `Banque — ${selectedAccount.name}`;
    if (mode === 'cash' && selectedRegister) return `Caisse — ${selectedRegister.name}`;
    if (mode === 'sales' && selectedClient) return `Ventes — ${selectedClient.name}`;
    if (mode === 'purchases' && selectedSupplier) return `Achats — ${selectedSupplier.name}`;
    if (mode === 'ar' && selectedClient) return `Encaiss. — ${selectedClient.name}`;
    if (mode === 'ap' && selectedSupplier) return `Règl. — ${selectedSupplier.name}`;
    return `${mode.charAt(0).toUpperCase() + mode.slice(1)} — (aucun)`;
  }, [mode, selectedAccount, selectedRegister, selectedClient, selectedSupplier]);

  const leftTitle = {
    bank: 'Comptes bancaires', cash: 'Caisses',
    sales: 'Clients (Ventes)', purchases: 'Fournisseurs (Achats)',
    ar: 'Clients (Encaiss.)', ap: 'Fournisseurs (Règl.)'
  }[mode] || 'Liste';

  const leftList = useMemo(() => {
    const list =
      mode === 'bank' ? accounts :
      mode === 'cash' ? registers :
      (mode === 'sales' || mode === 'ar') ? clients :
      (mode === 'purchases' || mode === 'ap') ? suppliers : [];
    return Array.isArray(list) ? list : [];
  }, [mode, accounts, registers, clients, suppliers]);

  const selectedItem = useMemo(() => {
    return mode === 'bank' ? selectedAccount :
           mode === 'cash' ? selectedRegister :
           (mode === 'sales' || mode === 'ar') ? selectedClient :
           (mode === 'purchases' || mode === 'ap') ? selectedSupplier : null;
  }, [mode, selectedAccount, selectedRegister, selectedClient, selectedSupplier]);

  if (loadingLeft) {
    return <div className="min-h-screen bg-gray-50 p-6"><div>Chargement…</div></div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            <Icon name="IconBook" className="text-indigo-600 mr-3" />
            Écritures (V1 Complete)
          </h1>
          <p className="text-sm text-gray-600 mt-1">Poster Trésorerie + Ventes + Achats + Paiements.</p>
        </div>

        {/* Tab buttons */}
        <div className="flex bg-white border rounded-lg overflow-hidden">
          {[
            { key: 'bank', label: 'Banque' },
            { key: 'cash', label: 'Caisse' },
            { key: 'sales', label: 'Ventes' },
            { key: 'purchases', label: 'Achats' },
            { key: 'ar', label: 'Encaiss.' },
            { key: 'ap', label: 'Règl. frs' }
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                mode === key ? 'bg-blue-700 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setMode(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <Button
          onClick={() => selectedItem?._id && loadTransactions({ type: mode, id: selectedItem._id })}
          disabled={loadingTx || !selectedItem}
        >
          <Icon name="IconRefresh" className={`mr-2 ${loadingTx ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left list */}
        <div className="lg:col-span-1">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">{leftTitle}</h2>
              <Button
                onClick={refreshLeft}
                variant="ghost"
                size="sm"
                disabled={loadingLeft}
              >
                Recharger
              </Button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {leftList.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {loadingLeft ? 'Chargement...' : 'Aucun élément'}
                </div>
              ) : (
                leftList.map((item) => {
                  const itemId = item.id || item._id;
                  const isSelected = selectedItem?._id === itemId;
                  return (
                    <div
                      key={itemId || Math.random()}
                      onClick={() => {
                        if (mode === 'bank') setSelectedAccount(item);
                        else if (mode === 'cash') setSelectedRegister(item);
                        else if (mode === 'sales' || mode === 'ar') setSelectedClient(item);
                        else if (mode === 'purchases' || mode === 'ap') setSelectedSupplier(item);
                      }}
                      className={`p-4 rounded-lg cursor-pointer transition border-2 ${
                        isSelected ? 'bg-indigo-50 border-indigo-500' : 'bg-gray-50 hover:bg-gray-100 border-transparent'
                      }`}
                    >
                      <div className="font-semibold text-gray-800 truncate">{item.name || item.companyName || '—'}</div>
                      {mode === 'bank' && item.bankName && (
                        <>
                          <div className="text-sm text-gray-600">{item.bankName}</div>
                          <div className="text-xs text-gray-500">{item.accountNumber}</div>
                        </>
                      )}
                      {(mode === 'sales' || mode === 'ar') && item.mf && (
                        <div className="text-xs text-gray-500">{item.mf}</div>
                      )}
                      {(mode === 'purchases' || mode === 'ap') && item.mf && (
                        <div className="text-xs text-gray-500">{item.mf}</div>
                      )}
                      <div className="mt-2 text-right">
                        <div className="text-lg font-bold text-indigo-600">
                          {item.currentBalance !== undefined ? formatCurrency(item.currentBalance) : '—'}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>

        {/* Transactions */}
        <div className="lg:col-span-2">
          <Card>
            <h2 className="text-lg font-bold text-gray-800 mb-4">{selectedLabel}</h2>

            {loadingTx ? (
              <div className="text-gray-600 py-10 text-center">Chargement des transactions…</div>
            ) : Array.isArray(transactions) && transactions.length === 0 ? (
              <div className="text-gray-500 py-10 text-center">
                Aucune transaction. Sélectionne un compte/client.
              </div>
            ) : !Array.isArray(transactions) ? (
              <div className="text-red-500 py-10 text-center">Erreur données transactions</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transactions.map((tx) => {
                      const id = tx?.id || tx?._id;
                      const isPosting = postingId === id;
                      const posted = isTxPosted(tx);
                      const isIncome =
                        (mode === 'bank' || mode === 'cash')
                          ? tx.transactionType === 'income'
                          : (tx.totalHT || tx.amount || tx.totalTTC) > 0;

                      return (
                        <tr key={id || Math.random()} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {tx.date ? new Date(tx.date).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800 max-w-xs truncate">
                            {tx.description || tx.subject || tx.label || '-'}
                            {tx.reference && (
                              <div className="text-xs text-gray-500">Réf: {tx.reference}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold">
                            <span className={isIncome ? 'text-green-600' : 'text-red-600'}>
                              {isIncome ? '+' : '-'} {formatCurrency(tx.amount || tx.totalTTC || tx.totalHT || tx.total)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            {posted ? (
                              <Badge variant="success">Posté</Badge>
                            ) : (
                              <Badge variant="warning">À poster</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              onClick={() => handlePost(tx)}
                              disabled={isPosting || posted}
                              size="sm"
                              title={posted ? 'Déjà postée' : 'Poster en comptabilité'}
                            >
                              {posted ? 'Posté' : isPosting ? 'Posting…' : 'Poster'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
